/**
 * BẢN ĐỒ TỦ — GD-17 B3. Một thành phần, hai chỗ dùng: ô giữa trang Tổng quan và trang `/ban-do`
 * (nút "Xem toàn màn hình"). Không có bản thứ hai.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⛔ CHẤM TỦ VẼ KHI `lat/lng` HỢP LỆ (số, khác 0, trong -90..90 / -180..180) — GD-17 vòng 2,
 *   dev_device: KHÔNG phụ thuộc `gpsFix`. `gpsFix` chỉ nói NGUỒN của toạ độ, và nói ra trong popup:
 *   "Toạ độ tủ tự đo" (gpsFix) · "Toạ độ nhập tay" (còn lại, kể cả khi máy chủ chưa gửi gpsFix).
 *   "N tủ chưa có toạ độ" = đúng số tủ KHÔNG vẽ; "Chưa có tủ nào gửi toạ độ" chỉ khi N = tổng
 *   (và khi ấy dòng N không lặp lại cùng một điều).
 * ⛔ MÀU THEO LUẬT NGÂN SÁCH (tệp 29 điều cấm 9, chờ duyệt): xanh lá = đang nối · xám = mất kết
 *   nối · vàng = có việc cần xem · xám nhạt = chưa gán tuyến. **0 đỏ.** Chấm 10 px, viền 1 px màu
 *   nền. Màu đọc từ token lúc vẽ (Leaflet ghi `fill` bằng thuộc tính SVG — `var()` không chạy ở đó).
 * ⛔ NỀN BẢN ĐỒ HỎNG KHÔNG ĐƯỢC LÀM TRẮNG TRANG, KHÔNG ĐƯỢC SPAM CONSOLE: thử MỘT ô nền (z=0)
 *   trước khi gắn lớp nền; hỏng ⇒ không gắn lớp nào, vẽ lưới xám + "Không tải được nền bản đồ",
 *   chấm tủ vẫn vẽ. Kết quả thử giữ cho cả lượt mở trang (không thử lại theo vòng lặp). Lớp nền
 *   đã gắn mà ô nào đó lỗi (`tileerror`) ⇒ gỡ lớp, về cùng nhánh ấy.
 * `VITE_BAN_DO_NEN`: mẫu URL ô nền (`{z}/{x}/{y}`, `{s}` nếu có). Vắng ⇒ OSM QUA MÁY CHỦ (`/o-ban-do`, TK-26). `tat` hoặc rỗng ⇒
 *   không nền (máy chủ công ty không ra được internet).
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { AttributionControl, CircleMarker, MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import type { Alarm, TreeRoute } from '../lib/types';
import { TOA_DO_MAC_DINH } from '../lib/mattroi';

/*
 * TK-26 (10/10/2026): ô nền đi QUA MÁY CHỦ CỦA MÌNH (`/o-ban-do/…` → nginx → tile.openstreetmap.org, đệm 7 ngày).
 *   lee: máy bảng (app cài ra màn hình chính) "Không tải được nền bản đồ" dù TK-24 đã sửa phép thử và sw.js ⇒ máy bảng
 *   không gọi thẳng được máy chủ ô OSM. Cùng máy chủ thì cùng đường mạng và chứng chỉ trang đang chạy được.
 *   Dòng ghi công "© OpenStreetMap" GIỮ (điều khoản OSM) — điều kiện bên dưới so với hằng này.
 */
export const NEN_MAC_DINH = '/o-ban-do/{z}/{x}/{y}.png';

/** Mẫu URL nền đang dùng; `null` = tắt nền. */
export function mauNen(): string | null {
  const v = import.meta.env.VITE_BAN_DO_NEN as string | undefined;
  if (v === undefined) return NEN_MAC_DINH;
  const t = v.trim();
  return !t || t === 'tat' ? null : t;
}

type TrangThaiNen = 'dang-thu' | 'ok' | 'hong' | 'tat';

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 09/10/2026 — MỘT Ô HỎNG KHÔNG ĐƯỢC GIẾT CẢ NỀN BẢN ĐỒ.
 *
 * Lee dùng thật trên máy bảng: bản đồ chỉ còn các ô vuông. Trên máy bàn cùng trang thì chạy.
 * Dò ra ba chỗ cộng lại thành triệu chứng ấy, và cả ba đều ở phía mình:
 *
 *   1. Phép thử chỉ thử **một** ô (`0/0/0`) với hạn 8 giây. Một nhịp mạng xấu đúng lúc ấy là
 *      cả bản đồ tắt — dù mọi ô khác tải được.
 *   2. Kết quả giữ trong một `Promise` ở tầm mô-đun ⇒ **hỏng một lần là hỏng cả phiên**; chỉ
 *      đóng hẳn app mở lại mới thử lại. Trên app cài ra màn hình chính, "mở lại" không phải
 *      thao tác ai cũng nghĩ ra.
 *   3. `tileerror` của MỘT ô gọi thẳng `baoHong()` ⇒ gỡ luôn cả lớp nền.
 *
 * Chữa: thử tối đa 3 lần (0.6s → 1.8s), đếm lỗi thay vì tắt ngay, và cho một nút **Thử lại**.
 * ⛔ KHÔNG bỏ hẳn phép thử: nó là thứ phân biệt "nền chưa tải" với "nền trống" — bỏ đi thì
 *   lưới xám hiện ra mà không ai nói vì sao, đúng cái lee phải tự đoán lần này.
 */
const SO_LAN_THU = 3;
const SO_LOI_O_CHO_PHEP = 8;   // Leaflet tải ~20 ô một khung; vài ô hỏng là bình thường.

function thuMotO(mau: string, msCho: number): Promise<boolean> {
  return new Promise((xong) => {
    const img = new Image();
    const hen = window.setTimeout(() => { img.src = ''; xong(false); }, msCho);
    img.onload = () => { window.clearTimeout(hen); xong(true); };
    img.onerror = () => { window.clearTimeout(hen); xong(false); };
    // Ô thật ở vùng đang dùng, kèm tham số phá bộ nhớ đệm để lần "Thử lại" là một phép đo mới.
    img.src = mau
      .replace('{s}', 'a').replace('{z}', '6').replace('{x}', '51').replace('{y}', '31').replace('{r}', '')
      + (mau.includes('?') ? '&' : '?') + 'thu=' + Date.now();
  });
}

async function thuTaiNen(mau: string): Promise<boolean> {
  for (let i = 0; i < SO_LAN_THU; i++) {
    if (await thuMotO(mau, 6000)) return true;
    if (i < SO_LAN_THU - 1) await new Promise((r) => setTimeout(r, 600 * (i + 1) * 2));
  }
  return false;
}

function useNen(): { mau: string | null; tt: TrangThaiNen; baoHong: () => void; thuLai: () => void } {
  const mau = mauNen();
  const [tt, setTt] = useState<TrangThaiNen>(mau ? 'dang-thu' : 'tat');
  const [lan, setLan] = useState(0);
  const soLoi = useRef(0);
  useEffect(() => {
    if (!mau) return;
    let con = true;
    soLoi.current = 0;
    setTt('dang-thu');
    void thuTaiNen(mau).then((ok) => { if (con) setTt(ok ? 'ok' : 'hong'); });
    return () => { con = false; };
  }, [mau, lan]);
  return {
    mau,
    tt,
    /*
     * ⚠ ĐẾM, KHÔNG TẮT NGAY. `tileerror` bắn cho từng ô; vài ô rơi khi vừa kéo bản đồ là
     *   chuyện thường. Chỉ khi quá ngưỡng mới coi là nền hỏng.
     */
    baoHong: () => {
      soLoi.current += 1;
      if (soLoi.current > SO_LOI_O_CHO_PHEP) setTt('hong');
    },
    thuLai: () => { soLoi.current = 0; setLan((v) => v + 1); },
  };
}

export type LoaiCham = 'noi' | 'mat' | 'viec' | 'chua-gan';

/** Lớp token cho chú giải (chấm SVG) — cùng bốn loại với chấm trên bản đồ. */
export const CHU_GIAI: { loai: LoaiCham; ten: string; lop: string }[] = [
  { loai: 'noi', ten: 'Tủ đang nối', lop: 'text-tot' },
  { loai: 'mat', ten: 'Mất kết nối', lop: 'text-chu-3' },
  { loai: 'viec', ten: 'Có việc cần xem', lop: 'text-canh' },
  { loai: 'chua-gan', ten: 'Chưa gán tuyến', lop: 'text-vien-manh' },
];
const TOKEN_CUA: Record<LoaiCham, string> = { noi: '--tot', mat: '--chu-3', viec: '--canh', 'chua-gan': '--vien-manh' };

/** `rgb(r, g, b)` của một token `thietke.css` ("46 224 111") — đọc lúc vẽ để theo bộ sáng/tối. */
function mauToken(ten: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(ten).trim().split(/\s+/).join(', ');
  return v ? `rgb(${v})` : 'rgb(95, 117, 151)';
}

export interface ChamTu {
  id: string; ten: string; ma: string; tuyen: string | null;
  lat: number; lng: number; loai: LoaiCham; noi: boolean;
  /** `true` = toạ độ tủ tự đo (gpsFix); `false` = nhập tay / chưa biết nguồn. */
  tuDo: boolean;
}

/** Toạ độ vẽ được: số hữu hạn, khác 0, trong dải địa lý. Một chỗ quyết cho bản đồ. */
export function toaDoHopLe(lat: unknown, lng: unknown): boolean {
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)
    && lat !== 0 && lng !== 0 && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/** Tách tủ có/không định vị từ cây tuyến + cảnh báo đang mở. */
export function chamTuTuCay(cay: TreeRoute[] | undefined, canhBao: Alarm[] | undefined): { cham: ChamTu[]; khongToaDo: number; tong: number } {
  /* TK-25: hồ sơ "Mất kết nối" KHÔNG tô vàng chấm — tủ im là chấm XÁM (`mat`, đọc từ `status`), như cũ. Tô vàng nó
     là nói "có việc cần xem" về một tủ mà máy chủ không thấy gì để xem. */
  const coViec = new Set((canhBao ?? []).filter((a) => a.code !== 'MAT_KET_NOI').map((a) => a.cabinetId));
  const cham: ChamTu[] = [];
  let khongToaDo = 0, tong = 0;
  for (const r of cay ?? []) {
    for (const c of r.cabinets ?? []) {
      tong++;
      if (!toaDoHopLe(c.lat, c.lng)) { khongToaDo++; continue; }
      const noi = c.status === 'ONLINE';
      const loai: LoaiCham = coViec.has(c.id) ? 'viec' : noi ? 'noi' : 'mat';
      cham.push({ id: c.id, ten: c.name, ma: c.code, tuyen: r.tenDayDu ?? null, lat: c.lat as number, lng: c.lng as number, loai, noi, tuDo: c.gpsFix === true });
    }
  }
  return { cham, khongToaDo, tong };
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 TK-24 (09/10/2026) — GOM CỤM. Lee (report): *"Tôi giả sử các tủ tôi trải dài vài tỉnh, vài
 *   miền thì lúc đó bạn hiển thị làm sao cho tối ưu"*.
 *
 * Trước: mỗi tủ một chấm 5 px, không gom. 100 tủ trong một thành phố (đo trên dữ liệu giả bộ B,
 * ảnh `anh/truoc/tongquan_1440.png`) thành một đám chấm chồng nhau, không đếm, không bấm trúng.
 * Trải nhiều tỉnh thì `fitBounds` ôm cả nước và mỗi tỉnh là một vệt.
 *
 * Cách gom: chia MÀN HÌNH thành ô 56 px ở mức phóng hiện tại (`map.project`), tủ cùng ô thành một
 * cụm. Phóng vào thì ô 56 px bé lại theo mét ⇒ cụm tự tách. Không thêm thư viện (`package.json`
 * chỉ lee/dev_device sửa — AGENTS.md).
 * ⚠ Màu cụm = trạng thái XẤU NHẤT trong cụm (có việc > mất kết nối > chưa gán > đang nối): một cụm
 *   50 tủ xanh có 1 tủ cần xem thì cụm phải vàng, nếu không thì gom cụm giấu mất đúng cái tủ ấy.
 * ⚠ Chỉ tính lại khi ĐỔI MỨC PHÓNG, không tính khi kéo: ô chia theo toạ độ chiếu, không theo khung nhìn.
 * ⚠ Bấm cụm ⇒ phóng vào đúng khung của cụm (maxZoom 17); cụm nằm trong 1 ô 56 px ở zoom 17 là tủ
 *   thật sự chồng toạ độ — lúc ấy vẫn còn cụm và bấm sẽ mở popup liệt kê.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
const O_CUM_PX = 56;
const UU_TIEN_LOAI: LoaiCham[] = ['viec', 'mat', 'chua-gan', 'noi'];
interface CumTu { khoa: string; lat: number; lng: number; items: ChamTu[]; loai: LoaiCham; khung: L.LatLngBounds }

export function gomCum(chieu: (lat: number, lng: number) => { x: number; y: number }, cham: ChamTu[]): { don: ChamTu[]; cum: CumTu[] } {
  const o = new Map<string, ChamTu[]>();
  for (const c of cham) {
    const p = chieu(c.lat, c.lng);
    const k = `${Math.floor(p.x / O_CUM_PX)}:${Math.floor(p.y / O_CUM_PX)}`;
    const ds = o.get(k); if (ds) ds.push(c); else o.set(k, [c]);
  }
  const don: ChamTu[] = []; const cum: CumTu[] = [];
  for (const [khoa, items] of o) {
    if (items.length === 1) { don.push(items[0]); continue; }
    const lat = items.reduce((a, c) => a + c.lat, 0) / items.length;
    const lng = items.reduce((a, c) => a + c.lng, 0) / items.length;
    const loai = UU_TIEN_LOAI.find((l) => items.some((c) => c.loai === l)) ?? 'noi';
    cum.push({ khoa, lat, lng, items, loai, khung: L.latLngBounds(items.map((c) => [c.lat, c.lng] as [number, number])) });
  }
  return { don, cum };
}

/** Đường kính cụm theo số tủ: 30 px (2 tủ) → 46 px (≥ 100 tủ). Số nằm trong, chữ 12 px đậm. */
function coCum(so: number): number { return Math.min(46, 30 + Math.round(Math.log10(so) * 16)); }

function LopCham({ cham, mau, vien }: { cham: ChamTu[]; mau: Record<LoaiCham, string>; vien: string }) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  const { don, cum } = useMemo(
    () => gomCum((lat, lng) => map.project([lat, lng], zoom), cham),
    [cham, map, zoom],
  );
  return (
    <>
      {don.map((c) => (
        <CircleMarker
          key={c.id}
          center={[c.lat, c.lng]}
          radius={6}
          pathOptions={{ color: vien, weight: 1.5, fillColor: mau[c.loai], fillOpacity: 1, className: `cham-tu cham-${c.loai} cham-nguon-${c.tuDo ? 'tu-do' : 'nhap-tay'}` }}
        >
          <Popup>
            <div className="space-y-0.5 text-sm" data-popup-tu={c.id}>
              <p className="m-0 font-semibold">{c.ten} <span className="font-normal text-chu-3">({c.ma})</span></p>
              <p className="m-0 text-chu-2">{c.tuyen ?? 'Chưa gán tuyến'}</p>
              <p className="m-0 text-chu-2">{c.loai === 'viec' ? 'Có việc cần xem' : c.noi ? 'Đang nối' : 'Mất kết nối'}</p>
              <p className="m-0 text-chu-3" data-nguon-toa-do>{c.tuDo ? 'Toạ độ tủ tự đo' : 'Toạ độ nhập tay'}</p>
              <Link to={`/tu/${c.id}`} className="text-nhan hover:underline">Xem tủ</Link>
            </div>
          </Popup>
        </CircleMarker>
      ))}
      {cum.map((k) => {
        const d = coCum(k.items.length);
        const soViec = k.items.filter((c) => c.loai === 'viec').length;
        const soMat = k.items.filter((c) => !c.noi).length;
        const icon = L.divIcon({
          className: `cum-tu cum-${k.loai}`,
          iconSize: [d, d], iconAnchor: [d / 2, d / 2],
          html: `<span style="display:grid;place-items:center;width:${d}px;height:${d}px;border-radius:999px;background:${mau[k.loai]};border:2px solid ${vien};color:#fff;font-weight:600;font-size:12px;line-height:1;box-shadow:0 0 0 4px ${mau[k.loai]}33">${k.items.length}</span>`,
        });
        return (
          <Marker key={k.khoa} position={[k.lat, k.lng]} icon={icon}
            eventHandlers={{ click: () => { if (map.getZoom() < 17) map.fitBounds(k.khung, { padding: [32, 32], maxZoom: 17 }); } }}>
            <Popup>
              <div className="space-y-0.5 text-sm" data-popup-cum={k.khoa}>
                <p className="m-0 font-semibold">{k.items.length} tủ</p>
                <p className="m-0 text-chu-2">
                  {soViec > 0 ? `${soViec} có việc cần xem` : 'Không có việc cần xem'}
                  {soMat > 0 ? ` · ${soMat} mất kết nối` : ''}
                </p>
                {map.getZoom() >= 17 && (
                  <ul className="m-0 max-h-40 list-none overflow-y-auto p-0">
                    {k.items.map((c) => (
                      <li key={c.id}><Link to={`/tu/${c.id}`} className="text-nhan hover:underline">{c.ten}</Link> <span className="text-chu-3">({c.ma})</span></li>
                    ))}
                  </ul>
                )}
                {map.getZoom() < 17 && <p className="m-0 text-chu-3">Bấm để phóng vào</p>}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

/** Khớp khung theo các chấm; đổi kích thước ô thì báo Leaflet đo lại. */
function KhopKhung({ diem }: { diem: [number, number][] }) {
  const map = useMap();
  const khoa = diem.map((d) => d.join(',')).join('|');
  useEffect(() => {
    if (diem.length === 1) map.setView(diem[0], 15);
    else if (diem.length > 1) map.fitBounds(diem, { padding: [24, 24], maxZoom: 16 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [khoa, map]);
  useEffect(() => {
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

/** Lưới xám vẽ bằng SVG (không dùng dải màu — tệp 29 mục 1.3). */
const LUOI = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><path d="M32 0H0V32" fill="none" stroke="rgba(142,163,196,0.18)" stroke-width="1"/></svg>',
)}")`;

export function BanDoTu({ cay, canhBao, caoLop = 'h-[340px]', toanManHinh = false, diemKhung = null }: {
  cay: TreeRoute[] | undefined; canhBao: Alarm[] | undefined;
  /** Lớp chiều cao của khung (≥ 320 px). */
  caoLop?: string;
  toanManHinh?: boolean;
  /**
   * TK-25 — chip tỉnh trên Tổng quan: khung nhìn ôm ĐÚNG các điểm này (mọi chấm vẫn vẽ). `null`/rỗng ⇒ ôm
   *   toàn bộ như cũ. Lee mục 1: "các vùng xa nhau hiển thị sao cho trọn vẹn" — toàn bộ thì mỗi tỉnh là một
   *   cụm; bấm chip là phóng về tỉnh đó.
   */
  diemKhung?: [number, number][] | null;
}) {
  const nen = useNen();
  const { cham, khongToaDo, tong } = useMemo(() => chamTuTuCay(cay, canhBao), [cay, canhBao]);
  const khongTuNao = khongToaDo === tong;   // N = tổng (kể cả 0 tủ) ⇒ câu "Chưa có tủ nào…" thay dòng N
  const diem = useMemo(() => cham.map((c) => [c.lat, c.lng] as [number, number]), [cham]);
  const [mau, setMau] = useState<Record<LoaiCham, string> | null>(null);
  useEffect(() => {
    setMau({ noi: mauToken(TOKEN_CUA.noi), mat: mauToken(TOKEN_CUA.mat), viec: mauToken(TOKEN_CUA.viec), 'chua-gan': mauToken(TOKEN_CUA['chua-gan']) });
  }, []);
  const vien = useMemo(() => mauToken('--nen-1'), []);
  const coNen = nen.tt === 'ok';
  /*
   * ⚠ DỰNG LỚP NỀN NGAY TỪ LÚC CÒN ĐANG THỬ, không chờ phép thử xong.
   *   Phép thử là để BIẾT và để NÓI RA, không phải để cho phép. Chờ nó xong mới dựng lớp là
   *   bắt mọi người đợi thêm một vòng mạng, và nếu phép thử rơi thì ô bản đồ không bao giờ
   *   được thử lần nào — đúng chỗ hỏng mà lee gặp.
   */
  const veNen = nen.mau !== null && nen.tt !== 'hong' && nen.tt !== 'tat';

  return (
    <div className="space-y-2" data-ban-do>
      <div
        className={`relative isolate overflow-hidden rounded-nut border border-vien ${caoLop}
          [&_.leaflet-popup-content-wrapper]:rounded-nut [&_.leaflet-popup-content-wrapper]:bg-nen-1 [&_.leaflet-popup-content-wrapper]:text-chu-1
          [&_.leaflet-popup-tip]:bg-nen-1 [&_.leaflet-container]:font-[inherit]`}
      >
        <MapContainer
          center={[TOA_DO_MAC_DINH.lat, TOA_DO_MAC_DINH.lng]}
          zoom={11}
          attributionControl={false}
          className="h-full w-full"
          style={{ backgroundColor: 'rgb(var(--nen-2))', backgroundImage: coNen ? undefined : LUOI }}
        >
          <AttributionControl prefix={false} />
          {veNen && nen.mau && (
            <TileLayer
              url={nen.mau}
              attribution={nen.mau === NEN_MAC_DINH ? '© OpenStreetMap' : ''}
              eventHandlers={{ tileerror: () => nen.baoHong() }}
            />
          )}
          <KhopKhung diem={diemKhung && diemKhung.length > 0 ? diemKhung : diem} />
          {mau && <LopCham cham={cham} mau={mau} vien={vien} />}
        </MapContainer>

        {/* Chú giải + nút toàn màn hình — góc trên phải, trên lớp bản đồ */}
        <div className="pointer-events-none absolute right-2 top-2 z-[500] flex flex-col items-end gap-2">
          {!toanManHinh && (
            <Link to="/ban-do" className="pointer-events-auto rounded-nut border border-vien bg-nen-1 px-2.5 py-1 text-xs text-chu-1 hover:border-nhan/50">
              Xem toàn màn hình
            </Link>
          )}
          <ul className="pointer-events-auto m-0 list-none space-y-1 rounded-nut border border-vien bg-nen-1 px-2.5 py-2 text-xs text-chu-2" data-chu-giai>
            {CHU_GIAI.map((g) => (
              <li key={g.loai} className="flex items-center gap-1.5">
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className={g.lop}><circle cx="5" cy="5" r="5" fill="currentColor" /></svg>
                {g.ten}
              </li>
            ))}
          </ul>
        </div>

        {khongTuNao && (
          <div className="pointer-events-none absolute inset-0 z-[450] grid place-items-center">
            <p className="rounded-nut bg-nen-1 px-3 py-1.5 text-sm text-chu-2">Chưa có tủ nào gửi toạ độ</p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-chu-3">
        {nen.tt === 'dang-thu' && <span>Đang tải nền bản đồ…</span>}
        {nen.tt === 'hong' && (
          <span data-nen-hong className="flex items-center gap-2">
            Không tải được nền bản đồ
            <button type="button" onClick={nen.thuLai} className="rounded-nut border border-vien px-2 py-0.5 text-chu-2 hover:text-chu-1">
              Thử lại
            </button>
          </span>
        )}
        {nen.tt === 'tat' && <span>Nền bản đồ đang tắt</span>}
        {khongToaDo > 0 && !khongTuNao && <span data-khong-toa-do>{khongToaDo} tủ chưa có toạ độ</span>}
      </div>
    </div>
  );
}
